import { Dimension2D, DirectionalVector, Rectangle } from '../types/geo.types';
import { UIUtils } from './dom-utils';


export class EventAbsolutePosition {
    private readonly _pageX: number;
    private readonly _pageY: number;

    constructor(event: MouseEvent | any) {
        const genericEvent = UIUtils.toGenericMouseEvent(event);
        this._pageX = genericEvent.pageX;
        this._pageY = genericEvent.pageY;
    }

    getPageX() {
        return this._pageX;
    }

    getPageY() {
        return this._pageY;
    }
}

export type Pixel = number;

export class GeoUtils {

    public static getChildRectAbsolutePostion(parentRect: Rectangle, childRect: Rectangle): Rectangle {
        return {
            top: childRect.top - parentRect.top,
            left: childRect.left - parentRect.left,
            width: childRect.width,
            height: childRect.height,
        };
    }

    public static getRectIntersection(rect1: Rectangle, rect2: Rectangle): Rectangle {
        return {
            top: Math.min(rect1.top, rect2.top),
            left: Math.min(rect1.left, rect2.left),
            width: Math.min(rect1.width, rect2.width),
            height: Math.min(rect1.height, rect2.height),
        };
    }

    public static substractRects(rect1: Rectangle, rect2: Rectangle): Rectangle {
        return {
            top: rect1.top - rect2.top,
            left: rect1.left - rect2.left,
            width: rect1.width - rect2.width,
            height: rect1.height - rect2.height,
        };
    }

    public static flipDimension2D(geo: Rectangle | Dimension2D): Dimension2D {
        return {
            width: geo.height,
            height: geo.width,
        };
    }

    /**
     * Create a dimension values for given rect, that matches ratio of between source and target
     * @param source 
     * @param target 
     * @param rect 
     * @returns 
     */
    public static matchRatio(source: Dimension2D, target: Dimension2D, rect: Dimension2D){
        return {
            width: rect.width * target.width / source.width,
            height: rect.height * target.height / source.height,
        }
    }

    /** 
     * Returns a rectangle that fits with the target but keeps the aspect ratio of the source.
     * If target already contained inside the source, then returns the source
     */
    public static expandSourceToFitTarget(source: Dimension2D, target: Dimension2D): Dimension2D {
        if(this.containsDimension(source, target)) {
            return source;
        }

        const widthMajor = {
            width: target.width,
            height: this.findHeight(source, target.width),
        }

        const heightMajor = {
            width: this.findWidth(source, target.height),
            height: target.height,
        }

        if(this.containsDimension(widthMajor, target)) {
            return widthMajor;
        } else if(this.containsDimension(heightMajor, target)) {
            return heightMajor;
        } else {
            throw Error('Something went wrong while expanding source dim to target dim.');
        }
    }

    /** 
     * Returns a rectangle that fits with in the given source but keeps the aspect ratio of the target
     */
    public static fitInSource(source: Dimension2D, target: Dimension2D): Dimension2D {
        return this.containChild(source, target);
    }

    private static findWidth(source: Dimension2D, height: number): number {
        return height * source.width / source.height;
    }

    private static findHeight(source: Dimension2D, width: number): number {
        return width * source.height / source.width;
    }

    private static containChild(parent: Rectangle | Dimension2D, child: Rectangle | Dimension2D): Dimension2D {
        const r1 = parent.width / child.width;
        const r2 = parent.height / child.height;

        const r = r1 < r2 ? r1 : r2;
        return {
            height: child.height * r,
            width: child.width * r,
        };
    }

    public static containsDimension(source: Rectangle | Dimension2D, target: Rectangle | Dimension2D) {
        return source.width >= target.width && source.height >= target.height
    }

    public static containsRect(source: Rectangle, target: Rectangle) {
        if(source.top < target.top && source.left < target.left) {
            return source.width + source.left >= target.width + target.left 
                && source.height + source.top >= target.height + target.top;
        }
        
    }

    public static equals(source: Rectangle | Dimension2D, target: Rectangle | Dimension2D) {
        return source.width == target.width && source.height == target.height
    }

    public static containAbsolutePositionedChild(parent: Rectangle, child: Rectangle | Dimension2D): Rectangle {
        const r1 = parent.width / child.width;
        const r2 = parent.height / child.height;

        const r = r1 < r2 ? r1 : r2;
        const childHeight = child.height * r;
        const childWidth = child.width * r;
        return {
            top: (parent.height - childHeight) / 2,
            left: (parent.width - childWidth) / 2,
            height: childHeight,
            width: childWidth,
        };
    }

    public static coverAbsolutlyPositionedChild(parent: Rectangle | Dimension2D, child: Rectangle | Dimension2D): Rectangle {
        const r1 = parent.width / child.width;
        const r2 = parent.height / child.height;

        const r = r1 > r2 ? r1 : r2;
        const childHeight = child.height * r;
        const childWidth = child.width * r;
        return {
            top: (childHeight - parent.height) / 2,
            left: (childWidth - parent.width) / -2,
            height: childHeight,
            width: childWidth,
        };
    }

    /**
     * Returns delta
     * @param event1
     * @param event2
     */
    public static deltaEventData(startEvent: EventAbsolutePosition, endEvent: EventAbsolutePosition): DirectionalVector {
        return {
            x: endEvent.getPageX() - startEvent.getPageX(),
            y: endEvent.getPageY() - startEvent.getPageY(),
        };
    }
}
